/**
 * Per-typeId validity check for a filled form, plus a tree walk that
 * gathers every required-but-unsatisfying element id (with a page-jump
 * map for Pages elements).
 *
 * Pure JS — no React, no DOM. Ported verbatim from
 * apps/web/src/components/views/DocumentView/validation.ts.
 */

import type {
  DocumentElement,
  LogicRule,
} from "./types";
import { forEachVisibleLeaf } from "./logic/visibility";
import {
  ANSWER_SENTINEL,
  ANSWERABLE_TYPE_IDS,
  ELEMENT_TYPE,
} from "./elementTypes";

const OTHER_VALUE = ANSWER_SENTINEL.other;
const NONE_VALUE = ANSWER_SENTINEL.none;

const isNonEmptyString = (v: unknown): boolean =>
  typeof v === "string" && v.trim() !== "";

// Lightweight email shape check — not RFC 5322 strict, just enough
// to catch obvious typos. Requires `local@domain.tld` with no spaces.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmail = (v: unknown): boolean =>
  typeof v === "string" && EMAIL_RE.test(v.trim());

/**
 * Per-typeId validity check for an element's stored value. Returns
 * `true` when the value satisfies the element's `required` constraint.
 * Unknown element types are treated as valid (we only block on types
 * we explicitly know how to validate).
 */
export function isElementValueValid(
  typeId: string,
  value: unknown,
  data: unknown,
): boolean {
  switch (typeId) {
    case ELEMENT_TYPE.shortTextInput:
    case ELEMENT_TYPE.longTextInput:
    case ELEMENT_TYPE.phoneInput:
    case ELEMENT_TYPE.urlInput:
      return isNonEmptyString(value);

    case ELEMENT_TYPE.emailInput:
      return isEmail(value);

    case ELEMENT_TYPE.nameInput: {
      const v = (value as { firstName?: unknown; lastName?: unknown }) || {};
      return isNonEmptyString(v.firstName) && isNonEmptyString(v.lastName);
    }

    case ELEMENT_TYPE.numberInput: {
      // NumberInput stores its value as the raw input STRING; resumed or
      // server payloads may carry a number. Accept either, as long as it
      // parses to a finite number. (Requiring `typeof === "number"` left every
      // filled number/currency field failing required validation.)
      if (typeof value === "number") return Number.isFinite(value);
      if (typeof value === "string") {
        const t = value.trim();
        return t !== "" && Number.isFinite(Number(t));
      }
      return false;
    }

    case ELEMENT_TYPE.currency:
    case ELEMENT_TYPE.slider: {
      // Raw numeric value stored as a string (or number) — like NumberInput.
      if (typeof value === "number") return Number.isFinite(value);
      if (typeof value === "string") {
        const t = value.trim();
        return t !== "" && Number.isFinite(Number(t));
      }
      return false;
    }

    case ELEMENT_TYPE.rating: {
      // Whole-number rating (stored as a string); cleared ⇒ "" ⇒ invalid.
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) && n >= 1;
    }

    case ELEMENT_TYPE.country:
      // ISO alpha-2 code string.
      return isNonEmptyString(value);

    case ELEMENT_TYPE.geoTag: {
      // Captured location: { lat, lng } with finite numbers.
      const v = value as { lat?: unknown; lng?: unknown } | null;
      return (
        !!v &&
        typeof v === "object" &&
        typeof v.lat === "number" &&
        Number.isFinite(v.lat) &&
        typeof v.lng === "number" &&
        Number.isFinite(v.lng)
      );
    }

    case ELEMENT_TYPE.contact:
    case ELEMENT_TYPE.job:
    case ELEMENT_TYPE.asset:
    case ELEMENT_TYPE.user:
    case ELEMENT_TYPE.project:
    case ELEMENT_TYPE.customer:
    case ELEMENT_TYPE.managedProperty: {
      // Rich-entry array of `{ id, name, ... }` snapshots. Required is
      // satisfied when at least `minimum` (default 1) entries carry a
      // non-empty `id`. `minimum` only applies when `allowMultiple` is
      // on — single-select fields just need one pick.
      const cfg =
        (data as { allowMultiple?: boolean; minimum?: unknown }) || {};
      const minRaw =
        cfg.minimum === "" || cfg.minimum === null || cfg.minimum === undefined
          ? NaN
          : Number(cfg.minimum);
      const minPicks =
        cfg.allowMultiple && Number.isFinite(minRaw) && minRaw > 0
          ? Math.floor(minRaw)
          : 1;
      if (Array.isArray(value)) {
        const filled = value.filter(
          (e) =>
            e !== null &&
            typeof e === "object" &&
            isNonEmptyString((e as { id?: unknown }).id),
        ).length;
        return filled >= minPicks;
      }
      return false;
    }

    case ELEMENT_TYPE.radioCards: {
      // Rich-entry array of `{ value, label, description? }`; a
      // single picked card carries one entry whose `value` is the
      // option's stored value. Legacy bare-string values fall through.
      if (Array.isArray(value)) {
        return value.some(
          (e) =>
            e !== null &&
            typeof e === "object" &&
            isNonEmptyString((e as { value?: unknown }).value),
        );
      }
      return isNonEmptyString(value);
    }

    case ELEMENT_TYPE.dropdownSelect: {
      // Rich-entry shape (current): array of `{ value, label, ... }`
      // snapshots, one per pick. Required is satisfied when at least
      // `minimum` (default 1) entries carry a non-empty `value`. The
      // ceiling lives on element.data.minimum, set by the configForm
      // when both `allowMultiple` and `required` are on. Bare-string
      // legacy values fall through the isNonEmptyString tail.
      const cfg =
        (data as { allowMultiple?: boolean; minimum?: unknown }) || {};
      // configForm number-input persists as string — coerce.
      const minRaw =
        cfg.minimum === "" || cfg.minimum === null || cfg.minimum === undefined
          ? NaN
          : Number(cfg.minimum);
      const minPicks =
        cfg.allowMultiple && Number.isFinite(minRaw) && minRaw > 0
          ? Math.floor(minRaw)
          : 1;
      if (Array.isArray(value)) {
        const filled = value.filter(
          (e) =>
            e !== null &&
            typeof e === "object" &&
            isNonEmptyString((e as { value?: unknown }).value),
        ).length;
        return filled >= minPicks;
      }
      return minPicks <= 1 && isNonEmptyString(value);
    }

    case ELEMENT_TYPE.multiChoice: {
      // Rich-entry shape (current): array of `{ value, label, other, none, ... }`.
      // Legacy shapes still tolerated: `{ selected, other }` envelope,
      // bare string array, single string. An "Other" pick with no
      // typed text is treated as empty.
      const v = value as
        | Array<{ value?: unknown; other?: boolean; none?: boolean }>
        | { selected?: string[]; other?: string }
        | string[]
        | string
        | undefined;

      if (Array.isArray(v)) {
        const arr = v as unknown[];
        // Rich entries: each pick is an object with a `value` (or
        // `other`/`none` sentinel flag). At least one pick with a
        // non-empty `value` (or a None entry) counts.
        const isRichArray =
          arr.length > 0 &&
          arr.every(
            (e) => e !== null && typeof e === "object" && !Array.isArray(e),
          );
        if (isRichArray) {
          const richEntries = arr as Array<{
            value?: unknown;
            other?: boolean;
            none?: boolean;
          }>;
          return richEntries.some((e) => {
            if (e.none) return true;
            return isNonEmptyString(e.value);
          });
        }
        // Legacy: array of bare strings.
        return arr.some((x) => typeof x === "string" && x !== "");
      }

      let selected: string[] = [];
      if (typeof v === "string") {
        selected = v ? [v] : [];
      } else if (v && typeof v === "object" && Array.isArray(v.selected)) {
        selected = v.selected.filter(
          (x): x is string => typeof x === "string" && x !== "",
        );
      }
      if (selected.length === 0) return false;
      const onlyOther =
        selected.length === 1 &&
        selected[0] === OTHER_VALUE &&
        !isNonEmptyString(
          (v as { other?: string } | undefined)?.other as string | undefined,
        );
      if (onlyOther) return false;
      void NONE_VALUE;
      return true;
    }

    case ELEMENT_TYPE.checkboxInput:
      return value === true;

    case ELEMENT_TYPE.toggleSwitch:
      return value !== undefined && value !== null;

    case ELEMENT_TYPE.addressInput: {
      const v = value as
        | { formatted?: unknown; addressLine1?: unknown }
        | undefined;
      return (
        isNonEmptyString(v?.formatted) || isNonEmptyString(v?.addressLine1)
      );
    }

    case ELEMENT_TYPE.datePicker:
    case ELEMENT_TYPE.timePicker:
    case ELEMENT_TYPE.dateTimePicker:
      return isNonEmptyString(value);

    case ELEMENT_TYPE.dateRange: {
      // The element stores `{ from, to }` (not `start`/`end`), and its
      // `openEndPolicy` decides which bounds are mandatory: 'both_required'
      // (default) needs both, 'open_start_ok' makes `from` optional, and
      // 'open_end_ok' makes `to` optional. Mirror the renderer's own required
      // check — the previous `start`/`end` keys never matched, so every
      // required date range was permanently unsatisfiable and blocked submit.
      const v = value as { from?: unknown; to?: unknown } | undefined;
      const policy = (data as { openEndPolicy?: string } | undefined)
        ?.openEndPolicy;
      const fromOk = policy === "open_start_ok" || isNonEmptyString(v?.from);
      const toOk = policy === "open_end_ok" || isNonEmptyString(v?.to);
      return fromOk && toOk;
    }

    // Palette widgets outside ELEMENT_TYPE: a toggle group answers with its
    // option value, colour bands with a non-empty list of bands.
    case "toggle-group":
      return typeof value === "string" && value.trim() !== "";
    case "colour-bands":
      return Array.isArray(value) && value.length > 0;

    case ELEMENT_TYPE.fileUpload:
    case ELEMENT_TYPE.imageUpload:
    case ELEMENT_TYPE.signature: {
      // Multiple uploads store an array of `{ fileId, name }` refs; single
      // uploads and every signature store one bare ref object. Both satisfy
      // required, so accept either shape. (Checking only `Array.isArray` left
      // required single uploads and signatures permanently unsatisfiable even
      // after a successful upload.) The id may be a number or a string —
      // attachment ids are numeric in current data but persisted responses and
      // GraphQL IDs can carry string ids, so accept either rather than reject
      // valid drafts.
      const hasRef = (v: unknown): boolean => {
        if (v === null || typeof v !== "object") return false;
        const id = (v as { fileId?: unknown }).fileId;
        return (
          (typeof id === "number" && Number.isFinite(id)) ||
          (typeof id === "string" && id.trim() !== "")
        );
      };
      return Array.isArray(value) ? value.some(hasRef) : hasRef(value);
    }

    default:
      void data;
      return true;
  }
}

/**
 * Map from a Pages element's id to the index of its first descendant
 * page that contains a validation error. Used by Pages to auto-switch
 * on submit failure so the filler lands on the page with the problem.
 */
export type PagesJumpMap = Map<string, number>;

export interface ValidationResult {
  invalidIds: string[];
  invalidIdSet: Set<string>;
  firstInvalidId: string | null;
  pageJumps: PagesJumpMap;
  /**
   * Number of required, visible, non-archived leaf elements visited by
   * the walker — i.e. the denominator for a "X of Y required filled"
   * progress display. Hidden / archived branches are excluded so the
   * total stays in sync with what the filler actually sees.
   */
  requiredTotal: number;
}

const EMPTY_RESULT: ValidationResult = {
  invalidIds: [],
  invalidIdSet: new Set(),
  firstInvalidId: null,
  pageJumps: new Map(),
  requiredTotal: 0,
};

/**
 * Walk the document tree, returning the in-order list of element ids
 * that are marked required but have an unsatisfying value, plus a map
 * of Pages elements → the page index that should be displayed to
 * bring the first failing descendant into view.
 */
export interface ValidateDocumentOptions {
  /**
   * Form-level "make every question required" override. When true, every
   * answerable element (see `answerableTypeIds`) is treated as required
   * regardless of its own `data.required` flag — per-element settings are
   * read-only here, never mutated. Presentation / container / output-only
   * elements are unaffected.
   */
  requiredOverride?: boolean;
  /**
   * The set of typeIds that count as answerable (can be forced required by
   * `requiredOverride`). This is catalog metadata — pass a set built from the
   * element-type catalog's `answerable` flag (the source of truth) so new
   * input types are recognised without a code change. When omitted, the
   * built-in {@link ANSWERABLE_TYPE_IDS} fallback is used.
   */
  answerableTypeIds?: ReadonlySet<string>;
  /**
   * The document's logic rules (`content.logic`). When supplied they are
   * re-evaluated against `values` so validation matches what the filler
   * actually saw: fields hidden by a fired `hide` action (or revealed by a
   * `show` action) are skipped/included accordingly, and `require` /
   * `unrequire` actions override the static `data.required` flag. Omit to
   * keep the legacy static-only behaviour.
   */
  logic?: LogicRule[];
}

export function validateDocument(
  elements: DocumentElement[] | undefined,
  values: Record<string, unknown>,
  options: ValidateDocumentOptions = {},
): ValidationResult {
  if (!elements || elements.length === 0) return EMPTY_RESULT;

  const requiredOverride = options.requiredOverride === true;
  // DB catalog `answerable` flags are authoritative when supplied; the
  // built-in set is the fallback for hosts that don't thread the catalog in.
  const answerableTypeIds = options.answerableTypeIds ?? ANSWERABLE_TYPE_IDS;
  const logicRules =
    Array.isArray(options.logic) && options.logic.length > 0
      ? options.logic
      : undefined;

  const invalidIds: string[] = [];
  const pageJumps: PagesJumpMap = new Map();
  let requiredTotal = 0;

  // `scopeValues` is the values object at the current repeat scope — the root
  // values at the top level, or a single repeat-instance object once we
  // descend into a repeatable section. Leaf lookups + nested sections resolve
  // against it so fields inside a repeat validate against their own instance
  // (values live at `values[sectionId][index][fieldId]`, not the bare id).
  // `resolved` is the logic evaluation for that same scope (root values, or
  // root merged with the instance slice — mirroring ResolvedStateProvider).
  // The shared visibility walk decides what the filler saw (hide precedence,
  // hidden containers, archived elements, repeat instances); an untouched
  // repeat counts as the one empty instance the fill shows by default, so a
  // required field inside it still flags.
  forEachVisibleLeaf(
    { elements, logic: logicRules },
    values,
    ({ element: el, values: scopeValues, state: logicState, ancestors }) => {
      // A locked field cannot be answered, so it is never required (the
      // renderers grey it out and block input). A fired enable/disable action
      // wins over the element's own flag, as in the dispatchers.
      const typeId = el.typeId;
      const data = ((el.data as Record<string, unknown>) || {}) as {
        required?: boolean;
        disabled?: boolean;
      };
      if ((logicState?.disabled ?? data.disabled) === true) return;

      // With the form-level override on, every answerable input counts as
      // required even if its own flag is off. Non-answerable leaves
      // (presentation / download / display) are never forced. A fired
      // require/unrequire logic action overrides the element's own flag
      // (matching the fill renderer's merge), but not the form-level override.
      const elementRequired =
        logicState?.required !== undefined
          ? logicState.required
          : data.required === true;
      const isRequired =
        elementRequired || (requiredOverride && answerableTypeIds.has(typeId));
      if (!isRequired) return;
      requiredTotal++;
      // Filler answers ride a uniform `{ value }` envelope — see
      // `useElementValue`. Unwrap before validating so the per-typeId
      // checks keep operating on the bare answer shape. Bare legacy
      // values pass through as-is for forwards-compat with
      // pre-envelope drafts in memory.
      const rawEntry = scopeValues[el.id];
      const isEnvelope =
        rawEntry !== null &&
        typeof rawEntry === "object" &&
        "value" in (rawEntry as Record<string, unknown>);
      const value = isEnvelope
        ? (rawEntry as { value: unknown }).value
        : rawEntry;
      if (isElementValueValid(typeId, value, el.data)) return;
      invalidIds.push(el.id);

      // The first invalid field under each Pages element decides the page
      // its jump lands on: the page ancestor directly under the pages one.
      for (let i = 0; i < ancestors.length - 1; i++) {
        const pages = ancestors[i].element;
        if (pages.typeId !== ELEMENT_TYPE.pages || pageJumps.has(pages.id)) continue;
        const page = ancestors[i + 1].element;
        const index = ((pages.elements || []) as DocumentElement[]).indexOf(page);
        if (index >= 0) pageJumps.set(pages.id, index);
      }
    },
    { emptyRepeatAsOneInstance: true },
  );

  return {
    invalidIds,
    invalidIdSet: new Set(invalidIds),
    firstInvalidId: invalidIds[0] ?? null,
    pageJumps,
    requiredTotal,
  };
}

/**
 * How many questions a form offers a filler: answerable, non-archived
 * elements anywhere in the tree, static and logic ignored (a design-time
 * count). Zero means there is nothing to fill; publishing refuses it.
 * Mirrors FormDocumentResult::countAnswerableElements in ISM.
 */
export function countAnswerableElements(
  elements: DocumentElement[] | undefined,
  answerableTypeIds: ReadonlySet<string> = ANSWERABLE_TYPE_IDS,
): number {
  let count = 0;
  const walk = (els: DocumentElement[] | undefined): void => {
    for (const el of els ?? []) {
      if (!el || typeof el !== "object") continue;
      if ((el as { archived?: boolean }).archived) continue;
      if (answerableTypeIds.has(el.typeId)) count++;
      walk(el.elements as DocumentElement[] | undefined);
    }
  };
  walk(elements);
  return count;
}
